import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, BookBookmark, LinkSimple } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Bibliotheque() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", url: "", category: "", type: "document" });
  const canManage = user.role === "admin" || user.role === "professeur";

  const load = () => api.get("/resources").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    try { await api.post("/resources", form); toast.success("Ressource ajoutée"); setOpen(false); setForm({ title: "", description: "", url: "", category: "", type: "document" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/resources/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-6" data-testid="library-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Campus virtuel</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Bibliothèque numérique</h1>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-resource-btn"><Plus size={18} className="mr-2" /> Ajouter</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouvelle ressource</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="resource-title" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div><Label>Lien (URL)</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="resource-url" /></div>
                <div><Label>Catégorie</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Management, Finance..." /></div>
              </div>
              <DialogFooter><Button onClick={add} className="bg-primary hover:bg-primary/90" data-testid="save-resource-btn">Ajouter</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">Aucune ressource pour le moment.</div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((r) => (
            <div key={r.id} className="flex flex-col rounded-xl border border-border bg-card p-5" data-testid={`resource-${r.id}`}>
              <div className="mb-3 flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent"><BookBookmark size={22} weight="duotone" /></div>
                {r.category && <Badge variant="secondary">{r.category}</Badge>}
              </div>
              <h3 className="font-display text-lg font-bold">{r.title}</h3>
              <p className="mt-1 flex-1 text-sm text-muted-foreground">{r.description}</p>
              <p className="mt-2 text-xs text-muted-foreground">Ajouté par {r.added_by_name}</p>
              <div className="mt-3 flex items-center gap-2">
                <a href={r.url} target="_blank" rel="noreferrer" className="flex-1">
                  <Button variant="outline" className="w-full"><LinkSimple size={16} className="mr-2" /> Ouvrir</Button>
                </a>
                {canManage && <Button size="icon" variant="ghost" onClick={() => del(r.id)}><Trash size={16} className="text-accent" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
